import { Component } from "@theme/component";

/**
 * A custom element that manages a carousel.
 *
 * @extends {Component}
 */
export default class CarouselComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    // PERF (Claude R45): Swiper init forces synchronous layout -- it measures
    // every slide. Every homepage carousel sits below the fold (first at
    // ~1,857px), yet all of them initialised during module-graph startup.
    // Profiled 14 Aug: swiper-bundle carried 1,203ms of attributed main thread
    // in the cold GTmetrix run, only 7ms of it actual script -- the rest is
    // this layout storm, the bulk of the page's biggest blocking task.
    // Init now waits until the carousel is within 600px of the viewport. The
    // bundle is already downloaded and parsed by then, so unlike the reverted
    // R43 nothing needs re-initialising and the download path is untouched.
    // Immediate in the theme editor so Customize stays WYSIWYG.
    this.deferSlideHydration();
    this.deferInitUntilNear();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._r45Observer) {
      this._r45Observer.disconnect();
      this._r45Observer = null;
    }
    if (this._slideObserver) {
      this._slideObserver.disconnect();
      this._slideObserver = null;
    }
  }

  // Deferred off-screen slides, 2 Sep 2026. Slides 2..n of an opted-in carousel are rendered
  // inside <template data-ctr-slide> by blocks/ctr-carousel.liquid, so they fetch nothing.
  // Measured cause: the homepage's four-slide carousel pulled 227,582 B on a phone to show one
  // image, because `loading="lazy"` does not hold back a below-fold carousel -- Chrome's lazy
  // threshold on a slow connection is thousands of pixels wide and the section sits inside it.
  //
  // THE 1200px MARGIN MUST STAY LARGER THAN deferInitUntilNear's 600px. Swiper sizes itself from
  // slide geometry; if it initialised against empty slides the carousel would lay out wrongly.
  // Hydrating first is the entire safety argument, not a preference.
  hydrateSlides() {
    const templates = this.querySelectorAll("template[data-ctr-slide]");
    if (!templates.length) return;
    templates.forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }

  deferSlideHydration() {
    if (!this.querySelector("template[data-ctr-slide]")) return;
    const inEditor =
      window.Shopify &&
      (window.Shopify.designMode || window.Shopify.visualPreviewMode);
    if (inEditor || !("IntersectionObserver" in window)) {
      this.hydrateSlides();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        this._slideObserver = null;
        this.hydrateSlides();
      },
      { rootMargin: "1200px 0px" }
    );
    this._slideObserver = io;
    io.observe(this);
  }

  deferInitUntilNear() {
    if (this.swiper || this._r45Observer) return;
    const inEditor =
      window.Shopify &&
      (window.Shopify.designMode || window.Shopify.visualPreviewMode);
    if (inEditor || !("IntersectionObserver" in window)) {
      this.initCarousel();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        this._r45Observer = null;
        if (this.isConnected && !this.swiper) this.initCarousel();
      },
      { rootMargin: "600px 0px" }
    );
    this._r45Observer = io;
    io.observe(this);
  }

  getConfig() {
    // WIRE THE MODULE IF THE CONTROL IS SHOWN AT *EITHER* BREAKPOINT.
    //
    // These used to read the MOBILE flag alone, and the markup uses that same flag only
    // to add `m-hidden-mobile`. So turning arrows off on mobile - which is exactly what
    // was done to three homepage carousels on 28 Aug 2026 - also tore Swiper's navigation
    // module out on DESKTOP, where the arrows are still rendered and still visible.
    // Measured on live 147519504499: `swiper.params.navigation` false, `navigation.nextEl`
    // null, arrows 31px wide and clicking them moved the track 0px, while calling
    // `swiper.slideNext()` directly moved it -457.6px. Dead arrows, three sections,
    // every desktop visit, for a week.
    //
    // Visibility stays entirely CSS's job via m-hidden-mobile / m-hidden-desktop. Wiring
    // a control that is hidden at the current breakpoint costs nothing - it cannot be
    // clicked - and it is what makes the other breakpoint work.
    const navigation =
      this.showNavigation || this.showNavigationDesktop
        ? {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          }
        : null;
    const pagination =
      this.showPagination || this.showPaginationDesktop
        ? {
            el: ".swiper-pagination",
            clickable: true,
          }
        : null;
    const scrollbar = this.showScrollbar
      ? {
          el: ".swiper-scrollbar",
        }
      : null;

    const autoplay = this.autoplay
      ? {
          delay: this.autoplayDelay,
        }
      : null;
    return {
      // UX (Claude R17): grab hand on hover so desktop users can tell the carousel
      // is draggable. Swiper's mousewheel module stays OFF on purpose -- enabling it
      // without forceToAxis hijacks vertical page scroll when the cursor is over a
      // carousel, which is worse than the problem it solves.
      grabCursor: true,
      loop: this.loop,
      slidesPerView: this.slidesAuto ? "auto" : this.slidesPerView,
      spaceBetween: this.spaceBetween,
      centeredSlides: this.centeredSlides,
      initialSlide: this.initialSlide,
      autoplay: autoplay || false,
      navigation: navigation || false,
      pagination: pagination || false,
      scrollbar: scrollbar || false,
      breakpoints: {
        768: {
          slidesPerView: this.slidesAuto ? "auto" : this.slidesPerViewDesktop,
          spaceBetween: this.spaceBetweenDesktop,
        },
      },
    };
  }

  initCarousel() {
    // Safety net: hydration is observed at 1200px and init at 600px, so this should
    // always be a no-op. It is here so a future change to either margin cannot silently
    // hand Swiper empty slides to measure.
    this.hydrateSlides();

    if (this.swiper) return;
    // R45: the bundle is a deferred classic script, so on a slow connection the
    // observer can fire before it has executed. Retry briefly instead of throwing.
    if (typeof Swiper === "undefined") {
      this._r45Retries = (this._r45Retries || 0) + 1;
      if (this._r45Retries <= 50) setTimeout(() => this.initCarousel(), 100);
      return;
    }
    // @ts-ignore
    const swiper = new Swiper(
      this.querySelector("[ref='swiperCarousel']"),
      this.getConfig()
    );

    this.swiper = swiper;
  }
  get loop() {
    const loop = this.getAttribute("loop");
    if (!loop) throw new Error("Loop is required");
    return loop === "true" ? true : false;
  }

  get slidesAuto() {
    const slidesAuto = this.getAttribute("slides-auto");
    if (!slidesAuto) throw new Error("Slides auto is required");
    return slidesAuto === "true" ? true : false;
  }

  get slidesPerView() {
    const slidesPerView = this.getAttribute("slides-per-view");
    if (!slidesPerView) throw new Error("Slides per view is required");
    return Number(slidesPerView);
  }

  get slidesPerViewDesktop() {
    const slidesPerViewDesktop = this.getAttribute("slides-per-view-desktop");
    if (!slidesPerViewDesktop)
      throw new Error("Slides per view desktop is required");
    return Number(slidesPerViewDesktop);
  }

  get spaceBetween() {
    const spaceBetween = this.getAttribute("space-between");
    if (!spaceBetween) throw new Error("Space between is required");
    return Number(spaceBetween);
  }

  get spaceBetweenDesktop() {
    const spaceBetweenDesktop = this.getAttribute("space-between-desktop");
    if (!spaceBetweenDesktop)
      throw new Error("Space between desktop is required");
    return spaceBetweenDesktop;
  }

  get centeredSlides() {
    const centeredSlides = this.getAttribute("centered-slides");
    if (!centeredSlides) throw new Error("Centered slides is required");
    return centeredSlides === "true" ? true : false;
  }

  get initialSlide() {
    const initialSlide = this.getAttribute("initial-slide");
    if (!initialSlide) throw new Error("Initial slide is required");

    return Number(initialSlide);
  }

  get autoplay() {
    const autoplay = this.getAttribute("autoplay");
    if (!autoplay) throw new Error("Autoplay is required");
    return autoplay === "true" ? true : false;
  }

  get autoplayDelay() {
    const autoplayDelay = this.getAttribute("autoplay-delay");
    if (!autoplayDelay) throw new Error("Autoplay delay is required");
    return Number(autoplayDelay);
  }

  get showNavigation() {
    const showNavigation = this.getAttribute("show-navigation");
    if (!showNavigation) throw new Error("Show navigation is required");
    return showNavigation === "true" ? true : false;
  }

  get showPagination() {
    const showPagination = this.getAttribute("show-pagination");
    if (!showPagination) throw new Error("Show pagination is required");
    return showPagination === "true" ? true : false;
  }

  // Deliberately tolerant where the others throw: if the attribute is absent - older
  // cached markup, or a caller that has not been updated - fall back to the mobile flag,
  // which is exactly the behaviour these carousels had before. Never throw for it.
  get showNavigationDesktop() {
    const v = this.getAttribute("show-navigation-desktop");
    if (v === null) return this.showNavigation;
    return v === "true";
  }

  get showPaginationDesktop() {
    const v = this.getAttribute("show-pagination-desktop");
    if (v === null) return this.showPagination;
    return v === "true";
  }

  get showScrollbar() {
    const showScrollbar = this.getAttribute("show-scrollbar");
    if (!showScrollbar) throw new Error("Show scrollbar is required");
    return showScrollbar === "true" ? true : false;
  }
}

if (!customElements.get("carousel-component")) {
  customElements.define("carousel-component", CarouselComponent);
}
